import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null = loading, false = logged out
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // admin "view as" persona: {label, patch}
  const [activeView, setActiveViewState] = useState(() => localStorage.getItem("pb_active_view") || null);

  const setActiveView = (v) => {
    const prev = activeView;
    setActiveViewState(v);
    localStorage.setItem("pb_active_view", v);
    api.post("/auth/active-view", { view: v }).catch(() => {
      setActiveViewState(prev);
      if (prev) localStorage.setItem("pb_active_view", prev);
    });
  };

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("pb_token");
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.active_view) {
        setActiveViewState(data.active_view);
        localStorage.setItem("pb_active_view", data.active_view);
      }
    } catch {
      localStorage.removeItem("pb_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check — GoogleCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  const login = async (token, u) => {
    localStorage.setItem("pb_token", token);
    setUser(u);
    await refresh();
  };

  const logout = () => {
    localStorage.removeItem("pb_token");
    localStorage.removeItem("pb_active_view");
    setActiveViewState(null);
    setPreview(null);
    setUser(false);
  };

  // One account, many hats — approved TDs/coaches (non-admin) switch between Player / Director / Coach views
  const isAdmin = user && user.role === "admin";
  const availableViews = user && !isAdmin
    ? ["player", ...(user.is_td ? ["td"] : []), ...(user.is_coach ? ["coach"] : [])]
    : ["player"];
  const canSwitchView = availableViews.length > 1;
  // no saved choice yet → pros land in their pro view (matches pre-switcher behavior)
  let view = activeView || (user && !isAdmin && user.is_td ? "td" : user && !isAdmin && user.is_coach ? "coach" : "player");
  if (!availableViews.includes(view)) view = "player";

  let effUser = user;
  if (preview && user) {
    effUser = { ...user, role: "player", is_coach: false, is_td: false, ...preview.patch };
  } else if (user && canSwitchView) {
    if (view === "player") effUser = { ...user, is_td: false, is_coach: false, member_view: true };
    else if (view === "td") effUser = { ...user, is_coach: false };
    else effUser = { ...user, is_td: false };
  }

  return (
    <AuthContext.Provider value={{ user: effUser, realUser: user, preview, setPreview, loading, login, logout, refresh, setUser,
      activeView: view, setActiveView, availableViews, canSwitchView }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
